import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {Source} from 'constants/SourceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelector from 'file-browser/FolderSelector';
import HelpButton from 'help/HelpButton';
import NavStore from 'nav/NavStore';
import FormGroup from 'ui-library/bs/FormGroup';
import ErrorMessage from 'ui-library/ErrorMessage';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import * as Headers from 'ui-library/Headers';
import FieldLabel from 'ui-library/inputs/FieldLabel';

function rootFolderIdToSourceId(sources: Array<Source>, rootFolderId: string | null): string | null {
  const matchedSource = sources.find((source) => source.rootFolder.api_identifier === rootFolderId);
  if (!matchedSource) {
    return null;
  }
  return matchedSource.id;
}

function sourceIdToRootFolderId(sources: Array<Source>, sourceId: string | null): string | null {
  const matchedSource = sources.find((source) => source.id === sourceId);
  if (!matchedSource) {
    return null;
  }
  return matchedSource.rootFolder.api_identifier;
}

class StorableSourceSelectorConnector extends DataConnector<
  StorableSourceSelectorProps,
  EnhancedStorableSourceSelectorProps
> {
  stores = [NavStore];

  enhanceProps(): EnhancedStorableSourceSelectorProps {
    const storeSources = this.navStore().getSources();
    return {
      sources: storeSources || [],
      areSourcesLoading: Boolean(storeSources == null),
    };
  }
}

export interface StorableSourceSelectorProps {
  error?: string;
  isWritable: boolean;
  onChange: (sourceId: string | null) => void;
  registryId: string | null;
  selectedSourceId: string | null;
  disableEditsWithTooltip?: string | null;
}
interface EnhancedStorableSourceSelectorProps {
  sources: Array<Source>;
  areSourcesLoading: boolean;
}
/**
 * Component displaying a project selector for storables. It allows the selection of a project or no project
 * ("None"), which interally sets the source to the storable's registry.
 *
 * Internally, it converts the `sourceId` of `selectedSourceId` to its `rootFolderId` and passes that to
 * `FolderSelector` component.
 */
export class StorableSourceSelector extends React.Component<
  StorableSourceSelectorProps & EnhancedStorableSourceSelectorProps
> {
  componentDidMount(): void {
    FileBrowserActions.fetchSources();
  }

  _handleFolderChange = (e: {target: {value: string | null}}): void => {
    const newSourceId = rootFolderIdToSourceId(this.props.sources, e.target.value);
    if (newSourceId) {
      this.props.onChange(newSourceId);
    } else {
      this.props.onChange(this.props.registryId);
    }
  };

  render(): JSX.Element {
    const value =
      this.props.registryId !== this.props.selectedSourceId
        ? sourceIdToRootFolderId(this.props.sources, this.props.selectedSourceId)
        : null;
    return (
      <Tooltip
        text={this.props.disableEditsWithTooltip}
        isDisabled={this.props.disableEditsWithTooltip == null}
      >
        <div>
          <FolderSelector
            allowNone={true}
            error={this.props.error}
            level="append"
            noFolderText="None"
            onChange={this._handleFolderChange}
            /* Read-only if not writable or loading, or has tooltip to disable */
            readOnly={
              !this.props.isWritable ||
              this.props.areSourcesLoading ||
              this.props.disableEditsWithTooltip != null
            }
            /* Only render projects (ALL) */
            showInventory={false}
            showNotebook={false}
            showRegistries={false}
            value={value}
            unsetOnFetchFail={false}
            checkExistingItemForDataModsMovement={true}
          />
        </div>
      </Tooltip>
    );
  }
}

const _popoverContent = (): JSX.Element => (
  <div>
    <p>
      Project to associate with the inventory item. Only users with read access to the project will be able to
      view the inventory item.
    </p>
    <p>
      If the project is not specified, then the inventory item defaults to the same permissions as its
      registry.
    </p>
  </div>
);

function ProjectHelpButton(): JSX.Element {
  return <HelpButton renderContent={_popoverContent} iconClassName="u-mini-spacing-left" />;
}

export function StorableSourceSelectorWithTitle(props: StorableSourceSelectorProps): JSX.Element {
  return (
    <div>
      <Headers.Title>
        Project
        <ProjectHelpButton />
      </Headers.Title>
      <ConnectedStorableSourceSelector {...props} />
    </div>
  );
}

interface StorableSourceFormGroupProps {
  error?: string;
  onStorableChange: ({sourceId}: {sourceId: string}) => void;
  registryId: string | null;
  selectedSourceId: string | null;
  isWritable?: boolean;
  className?: string;
}

export class StorableSourceFormGroup extends React.Component<StorableSourceFormGroupProps> {
  _handleProjectChange = (sourceId: string): void => {
    this.props.onStorableChange({sourceId});
  };

  render(): JSX.Element {
    return (
      <FormGroup className={this.props.className}>
        <FieldLabel
          isRequired={BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES}
          label="Project"
          labelStyle={FieldLabel.STYLES.LABEL}
        />
        <ProjectHelpButton />
        <ConnectedStorableSourceSelector
          error={this.props.error}
          isWritable={this.props.isWritable ?? true}
          onChange={this._handleProjectChange}
          registryId={this.props.registryId}
          selectedSourceId={this.props.selectedSourceId}
        />
        {this.props.error && <ErrorMessage error={this.props.error} />}
      </FormGroup>
    );
  }
}

export const ConnectedStorableSourceSelector =
  StorableSourceSelectorConnector.connect(StorableSourceSelector);
